import { colors, intl, MaybeUserProfile, ui, userApi } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'
import { StageControls } from 'room/common/stage-surface'
import ProfileAvatar from 'users/profile-avatar'
import {
	PollEyebrow,
	PollNote,
	PollQuestion,
	PollStageCard,
} from '../stage/poll-stage'
import { pollChart, PollResultRow, PollResults } from './poll-results'

const Rows = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 24px;
	list-style: none;
	margin: 32px 0 0;
	padding: 0;

	${ui.responsive.desktop1440} {
		gap: 28px;
		margin-top: 40px;
	}

	${ui.responsive.desktopFHD} {
		gap: 40px;
		margin-top: 56px;
	}
`

const Label = styled.div`
	align-items: baseline;
	display: flex;
	gap: 16px;
	justify-content: space-between;
	margin-bottom: 10px;
`

const AnswerText = styled.span`
	color: ${colors.blackish};
	font-size: 22px;
	font-weight: 500;
	line-height: 1.3;
	overflow-wrap: anywhere;

	${ui.responsive.desktop1440} {
		font-size: 24px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 32px;
	}
`

const Votes = styled.span`
	color: ${colors.warmGrey};
	flex: none;
	font-size: 16px;
	font-variant-numeric: tabular-nums;

	${ui.responsive.desktop1440} {
		font-size: 18px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

const Measure = styled.div`
	align-items: center;
	display: flex;
	gap: 20px;
`

const Bar = styled.div`
	background: ${colors.brightGray};
	border-radius: 8px;
	flex: 1;
	height: 24px;
	overflow: hidden;

	${ui.responsive.desktop1440} {
		height: 28px;
	}

	${ui.responsive.desktopFHD} {
		height: 40px;
		border-radius: 10px;
	}
`

/** The answer the room is leaning to stands out; the others step back. */
const Fill = styled.div<{ $leading: boolean; $percent: number }>`
	background: ${props =>
		props.$leading ? colors.indigoBlue : colors.indigoBlue30};
	border-radius: inherit;
	height: 100%;
	transition: width 0.4s ease-in-out;
	width: ${props => props.$percent}%;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Percent = styled.span`
	color: ${colors.blackish};
	flex: none;
	font-size: 24px;
	font-variant-numeric: tabular-nums;
	font-weight: 700;
	min-width: 3.2em;
	text-align: right;

	${ui.responsive.desktop1440} {
		font-size: 28px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 40px;
	}
`

/**
 * A column per answer, standing on one line and filling the card down to
 * its foot; the answers line up underneath, however many lines each takes.
 */
const Columns = styled.ul<{ $count: number }>`
	display: grid;
	flex: 1 1 auto;
	gap: 0 24px;
	grid-template-columns: repeat(${props => props.$count}, minmax(0, 1fr));
	grid-template-rows: minmax(0, 1fr) auto;
	list-style: none;
	margin: 32px 0 0;
	min-height: 280px;
	padding: 0;

	${ui.responsive.desktop1440} {
		gap: 0 32px;
		margin-top: 40px;
	}

	${ui.responsive.desktopFHD} {
		gap: 0 48px;
		margin-top: 48px;
	}
`

const Column = styled.li`
	display: grid;
	grid-row: span 2;
	grid-template-rows: subgrid;
	min-width: 0;
`

/**
 * The share's type size, with room above the column for it, so a full
 * column keeps its number in view.
 */
const Plot = styled.div`
	display: flex;
	flex-direction: column;
	font-size: 26px;
	min-height: 0;
	padding-top: 1.5em;

	${ui.responsive.desktop1440} {
		font-size: 30px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 44px;
	}
`

const Track = styled.div`
	align-self: center;
	background: ${colors.brightGray};
	border-radius: 10px 10px 4px 4px;
	flex: 1 1 auto;
	max-width: 200px;
	min-height: 0;
	position: relative;
	width: 100%;

	${ui.responsive.desktopFHD} {
		border-radius: 14px 14px 6px 6px;
		max-width: 260px;
	}
`

/** The answer the room is leaning to stands out; the others step back. */
const Rise = styled.div<{ $leading: boolean; $percent: number }>`
	background: ${props =>
		props.$leading ? colors.indigoBlue : colors.indigoBlue30};
	border-radius: inherit;
	bottom: 0;
	height: ${props => props.$percent}%;
	left: 0;
	position: absolute;
	right: 0;
	transition: height 0.4s ease-in-out;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

const Share = styled.span<{ $percent: number }>`
	bottom: calc(${props => props.$percent}% + 0.2em);
	color: ${colors.blackish};
	font-variant-numeric: tabular-nums;
	font-weight: 700;
	left: 50%;
	line-height: 1.2;
	position: absolute;
	transform: translateX(-50%);
	white-space: nowrap;
`

const ColumnLabel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	padding-top: 14px;
	text-align: center;

	${ui.responsive.desktopFHD} {
		padding-top: 20px;
	}
`

const Caption = styled.h3`
	color: ${colors.blackish};
	font-size: 20px;
	font-weight: 700;
	line-height: 1.2;
	margin: 0 0 8px;

	${ui.responsive.desktopFHD} {
		font-size: 26px;
	}
`

const Count = styled.p`
	color: ${colors.blackish};
	font-size: 64px;
	font-variant-numeric: tabular-nums;
	font-weight: 700;
	line-height: 1.1;
	margin: 0;

	${ui.responsive.desktopFHD} {
		font-size: 88px;
	}
`

const Faces = styled.ul`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	list-style: none;
	margin: 20px 0 0;
	padding: 3px;
`

const Face = styled.li`
	border-radius: 50%;
	box-shadow: 0 0 0 3px ${colors.greenishCyan};
	height: 40px;
	width: 40px;

	${ui.responsive.desktopFHD} {
		height: 52px;
		width: 52px;
	}
`

const Avatar = styled(ProfileAvatar)`
	border-radius: 50%;
	height: 100%;
	width: 100%;
`

const Base = styled.p`
	color: ${colors.warmGrey};
	font-size: 16px;
	line-height: 1.4;
	margin: 24px 0 0;

	${ui.responsive.desktopFHD} {
		font-size: 20px;
	}
`

const YourAnswer = styled.section`
	border-top: 1px solid ${colors.borderLightGray};
	margin-top: 32px;
	padding-top: 24px;
`

// Read by assistive tech, never shown: "3/4" on its own is a date to a screen reader
const VisuallyHidden = styled.span`
	height: 1px;
	overflow: hidden;
	position: absolute;
	width: 1px;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`

const ResultRow: FC<{ row: PollResultRow }> = ({ row }) => (
	<li>
		<Label>
			<AnswerText>{row.text}</AnswerText>
			<Votes>
				<intl.Translate count={row.votes}>polling.stage.votes</intl.Translate>
			</Votes>
		</Label>
		<Measure>
			<Bar
				role="progressbar"
				aria-label={row.text}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={row.percent}
			>
				<Fill $leading={row.leading} $percent={row.percent} />
			</Bar>
			<Percent aria-hidden="true">{row.percent}%</Percent>
		</Measure>
	</li>
)

const ResultColumn: FC<{ row: PollResultRow }> = ({ row }) => (
	<Column>
		<Plot>
			<Track
				role="progressbar"
				aria-label={row.text}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={row.percent}
			>
				<Rise $leading={row.leading} $percent={row.percent} />
				<Share aria-hidden="true" $percent={row.percent}>
					{row.percent}%
				</Share>
			</Track>
		</Plot>
		<ColumnLabel>
			<AnswerText>{row.text}</AnswerText>
			<Votes>
				<intl.Translate count={row.votes}>polling.stage.votes</intl.Translate>
			</Votes>
		</ColumnLabel>
	</Column>
)

type Props = {
	question: string
	/** The poll takes more than one answer from each. */
	multiple: boolean
	results: PollResults
	/** Everybody who may answer. */
	total: number
	/** Those who have answered, a face each. */
	respondents: MaybeUserProfile[]
	/** The trainer's own answer: on their window, never on the projector. */
	trainerAnswer?: ReactNode
}

/**
 * A multiple-choice poll on the in-person stage: the question, and under it
 * a column for every answer with its share of those who answered - a bar
 * each when the answers are many or long (`pollChart`); beside them, how
 * many have. The room answers on its own devices, so this is what it reads
 * its results from. The online card is `MultipleChoiceResults`.
 */
export const MultipleChoiceStageView: FC<Props> = ({
	question,
	multiple,
	results,
	total,
	respondents,
	trainerAnswer,
}) => {
	const t = intl.useTranslate()
	return (
		<PollStageCard
			testId="multiple_choice.stage"
			side={{
				label: t('polling.stage.summary'),
				content: (
					<>
						<Caption>
							<intl.Translate>polling.stage.answered</intl.Translate>
						</Caption>
						<Count>
							<span aria-hidden="true">
								{results.respondents}/{total}
							</span>
							<VisuallyHidden>
								<intl.Translate count={results.respondents} total={total}>
									polling.stage.answered_of
								</intl.Translate>
							</VisuallyHidden>
						</Count>
						{respondents.length ? (
							<Faces aria-label={t('polling.stage.answered')}>
								{respondents.map((profile, index) => {
									const name = profile ? userApi.getDisplayName(profile) : ''
									return (
										<Face key={index} title={name || undefined}>
											<Avatar profile={profile} />
											{name ? <VisuallyHidden>{name}</VisuallyHidden> : null}
										</Face>
									)
								})}
							</Faces>
						) : null}
						{results.respondents ? (
							<Base>
								<intl.Translate count={results.respondents}>
									polling.stage.base
								</intl.Translate>
							</Base>
						) : null}
						{trainerAnswer ? (
							<StageControls>
								<YourAnswer>
									<Caption>
										<intl.Translate>polling.stage.your_answer</intl.Translate>
									</Caption>
									{trainerAnswer}
								</YourAnswer>
							</StageControls>
						) : null}
					</>
				),
			}}
		>
			<PollEyebrow>
				<intl.Translate>polling.stage.eyebrow</intl.Translate>
			</PollEyebrow>
			<PollQuestion>{question}</PollQuestion>
			{multiple ? (
				<PollNote>
					<intl.Translate>polling.stage.multiple_hint</intl.Translate>
				</PollNote>
			) : null}
			{pollChart(results.rows) === 'columns' ? (
				<Columns $count={results.rows.length} data-chart="columns">
					{results.rows.map(row => (
						<ResultColumn key={row.answerId} row={row} />
					))}
				</Columns>
			) : (
				<Rows data-chart="bars">
					{results.rows.map(row => (
						<ResultRow key={row.answerId} row={row} />
					))}
				</Rows>
			)}
		</PollStageCard>
	)
}
